import React, { useState, useEffect } from 'react';
import { RefreshCw, Smartphone, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const WhatsAppQRCode = () => {
  const [hasQrCode, setHasQrCode] = useState(false);
  const [status, setStatus] = useState('loading');
  const [connected, setConnected] = useState(false);
  const [connectedPhone, setConnectedPhone] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [error, setError] = useState(null);
  const [qrTimestamp, setQrTimestamp] = useState(Date.now());

  // CRM_WHATSAPP_IDENTIDADE_VISUAL_V1
  const formatConnectedPhone = (value) => {
    const digits =
      String(value || '').replace(/\D/g, '');

    if (!digits) {
      return '';
    }

    let national = digits;

    if (
      digits.startsWith('55') &&
      (digits.length === 12 ||
       digits.length === 13)
    ) {
      national = digits.slice(2);
    }

    if (national.length === 11) {
      return (
        '+55 (' +
        national.slice(0, 2) +
        ') ' +
        national.slice(2, 7) +
        '-' +
        national.slice(7)
      );
    }

    if (national.length === 10) {
      return (
        '+55 (' +
        national.slice(0, 2) +
        ') ' +
        national.slice(2, 6) +
        '-' +
        national.slice(6)
      );
    }

    return '+' + digits;
  };

  const fetchQRCode = async () => {
    try {
      setStatus('loading');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/whatsapp/qrcode`);
      const data = await response.json();
      
      if (data.hasQrCode) {
        setHasQrCode(true);
        setQrTimestamp(Date.now());
        setStatus('ready');
      } else {
        setHasQrCode(false);
        setStatus('no_qrcode');
      }
    } catch (err) {
      setError('Erro ao carregar QR Code');
      setStatus('error');
    }
  };

  const fetchStatus = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/whatsapp/status`);
      const data = await response.json();
      setConnected(data.connected);
      setConnectedPhone(data.phone || null);
      setAccountName(data.accountName || null);
    } catch (err) {
      console.error('Erro ao verificar status:', err);
    }
  };

  useEffect(() => {
    fetchQRCode();
    fetchStatus();
    
    const interval = setInterval(() => {
      fetchQRCode();
      fetchStatus();
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const handleRegenerateQR = async () => {
    try {
      setStatus('loading');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      await fetch(`${apiUrl}/whatsapp/regenerate-qr`, {
        method: 'POST'
      });
      // Aguardar 3 segundos para o QR ser gerado
      setTimeout(() => {
        setQrTimestamp(Date.now());
        fetchQRCode();
        fetchStatus();
      }, 3000);
    } catch (err) {
      setError('Erro ao regenerar QR Code');
      setStatus('error');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Conexão WhatsApp
        </h3>
        <button
          onClick={connected ? fetchStatus : handleRegenerateQR}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={connected ? "Atualizar status" : "Regenerar QR Code"}
        >
          <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-600 dark:text-green-400 font-medium">Conectado</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-600 dark:text-red-400 font-medium">Desconectado</span>
            </>
          )}
        </div>

        {!connected && status === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {!connected && status === 'error' && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {!connected && status === 'no_qrcode' && (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum QR Code disponível</p>
            <p className="text-sm mt-1">O servidor está gerando um novo QR Code...</p>
          </div>
        )}

        {status === 'ready' && hasQrCode && !connected && (
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700">
              <img 
                src={`/qrcode.png?t=${qrTimestamp}`} 
                alt="QR Code WhatsApp" 
                className="w-48 h-48"
              />
            </div>
            <div className="mt-4 text-center space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Escaneie o QR Code com seu WhatsApp
              </p>
              <ol className="text-xs text-gray-500 dark:text-gray-500 text-left space-y-1">
                <li>1. Abra o WhatsApp no seu celular</li>
                <li>2. Vá em: Aparelhos conectados → Conectar um aparelho</li>
                <li>3. Escaneie o QR Code acima</li>
              </ol>
            </div>
          </div>
        )}

        {connected && (
          <div className="text-center py-8 text-green-600 dark:text-green-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-2" />
            <p className="font-medium">WhatsApp conectado com sucesso!</p>

            {connectedPhone && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Número conectado
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                  {formatConnectedPhone(connectedPhone)}
                </p>
              </div>
            )}

            {accountName && (
              <div className="mt-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Nome da conta
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">
                  {accountName}
                </p>
              </div>
            )}

            <p className="text-sm mt-4">O bot está pronto para enviar mensagens</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppQRCode;
